/* eslint-disable max-lines */
import { useCallback } from "react";
import { useAtom } from "jotai";
import { isUserLoggedIn } from "@design-lab/entities/user";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { CirclePlus, ChevronRight } from "lucide-react";
import { Card, CardContent, CardFooter } from "@design-lab/shared/ui/card";
import { Button } from "@design-lab/shared/ui";
import { MyDesignLibrary } from "@design-lab/widgets/design-lab-upload-artwork/ui/MyDesignLibrary";
import {
  MAX_TRANSFER_ARTWORKS,
  uploadModalOpenAtom,
  libraryModalOpenAtom,
  transfersManagerAtom
} from "@design-lab/entities/transfers-lab";
import { transfersManagerMachine } from "@design-lab/entities/transfers-lab/model/transfers-manager.machine";
import { TransferEntryCard } from "./TransferEntryCard";
import { useTransfersUpload } from "../model/useTransfersUpload";
import { UploadModal } from "./UploadModal";
import { TransferImageSelector } from "./TransferImageSelector";
import { UploadStatusDisplay } from "./UploadStatusDisplay";

export function TransfersLabArtworksUpload() {
  const {
    entries,
    selectedId,
    hasUploadingArtwork,
    uploadingCount,
    addFiles,
    addArtworksFromLibrary,
    cancelUpload,
    removeEntry,
    selectEntry
  } = useTransfersUpload();

  const [state, send] = useAtom(transfersManagerAtom);
  const nextTabInfo = transfersManagerMachine.selectors.nextTabInfo(state);
  const { isDesktop } = useResponsive();
  const isLoggedIn = isUserLoggedIn();
  const [showUploadModal, setShowUploadModal] = useAtom(uploadModalOpenAtom);
  const [showLibraryModal, setShowLibraryModal] = useAtom(libraryModalOpenAtom);

  const handleFileDrop = async (droppedFiles: File[]) => {
    if (!droppedFiles || droppedFiles.length === 0) return;
    await addFiles(droppedFiles);
  };

  const handleCanvaFilesReady = useCallback(
    async (files: File[]) => {
      await addFiles(files, "canva"); // Pass "canva" as source
    },
    [addFiles]
  );

  const handleLibraryClose = useCallback(() => {
    setShowLibraryModal(false);
  }, []);

  const handleLibrarySelect = useCallback(
    async (artworks: Array<{ id: number; fileType?: string }>) => {
      setShowLibraryModal(false);
      await addArtworksFromLibrary(artworks.map(artwork => artwork.id));
    },
    [addArtworksFromLibrary]
  );

  // Websocket routing handled inside useTransfersUpload

  return (
    <>
      <Card className="flex h-full flex-1 flex-col border-none shadow-none lg:border lg:shadow">
        <CardContent className="flex flex-1 flex-col overflow-y-auto pt-6">
          <div className="flex flex-1 flex-col gap-2">
            {entries.length === 0 ? (
              <TransferImageSelector
                title="Upload Artwork"
                onFileDrop={handleFileDrop}
                isLoggedIn={isLoggedIn}
                isDesktop={isDesktop}
                onCanvaFilesReady={handleCanvaFilesReady}
                onLibraryClick={() => setShowLibraryModal(true)}
                allowMultipleFiles={true}
              />
            ) : (
              <div className="rounded-[14px] bg-white">
                <UploadStatusDisplay
                  entries={entries}
                  hasUploadingArtwork={hasUploadingArtwork}
                  uploadingCount={uploadingCount}
                />
                <p className="mt-1 text-xs font-semibold text-gray-600">
                  Maximum {MAX_TRANSFER_ARTWORKS} images is allowed
                </p>

                <div className="mt-8 flex flex-wrap gap-[18px]">
                  {entries.map(entry => (
                    <TransferEntryCard
                      key={entry.id}
                      entry={entry}
                      isSelected={selectedId === entry.id}
                      onSelect={() => selectEntry(entry.id)}
                      onCancel={cancelUpload}
                      onRemove={removeEntry}
                    />
                  ))}

                  {/* Empty slots - always show total of MAX_TRANSFER_ARTWORKS slots */}
                  {Array.from({ length: Math.max(0, MAX_TRANSFER_ARTWORKS - entries.length) }).map(
                    (_, i) => (
                      <button
                        key={`empty-${i}`}
                        type="button"
                        onClick={() => setShowUploadModal(true)}
                        className="group relative flex h-[110px] w-[110px] items-center justify-center rounded-lg border border-dashed border-blue-600 bg-white transition-colors hover:bg-blue-50"
                        data-testid={`add-image-slot-${i}`}
                      >
                        <CirclePlus className="size-9 text-blue-600" />
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between gap-2 pt-6">
          <div className="flex items-center gap-2">
            <Button
              data-testid="next-button"
              variant="destructive"
              size="xl"
              isDisabled={hasUploadingArtwork || entries.length === 0 || !nextTabInfo.enabled}
              onPress={() => send({ type: "tab.next" })}
              className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardFooter>
      </Card>

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onFileDrop={handleFileDrop}
        onOpenLibrary={() => setShowLibraryModal(true)}
        onCanvaFilesReady={handleCanvaFilesReady}
        isLoggedIn={isLoggedIn}
        isDesktop={isDesktop}
      />

      <MyDesignLibrary
        isOpen={showLibraryModal}
        onClose={handleLibraryClose}
        onSelectArtworks={handleLibrarySelect}
        allowMultipleSelection={true}
        maxSelectionCount={MAX_TRANSFER_ARTWORKS - entries.length}
      />
    </>
  );
}
